import {
    Box,
    Button,
    Grid,
    List,
    ListItem,
    ListItemButton,
    Typography,
    Divider,
    CardContent,
    TablePagination,
    Chip,
} from "@mui/material";
import { AppCard } from '../../components/common/AppCard';
import { RecordContextProvider } from "react-admin";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

// Material UI Icons
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PriceChangeIcon from "@mui/icons-material/PriceChangeOutlined";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import CalendarTodayIcon from "@mui/icons-material/Today";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PendingIcon from "@mui/icons-material/Pending";
import HelpIcon from "@mui/icons-material/Help";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import PersonPinIcon from '@mui/icons-material/PersonPin';
import RedeemIcon from "@mui/icons-material/Redeem";
import TimelapseIcon from '@mui/icons-material/Timelapse';
import WhatshotIcon from "@mui/icons-material/Whatshot";
import GetAppIcon from '@mui/icons-material/GetApp';

import { AccountTransfer, capitalizeFirst } from "../../types";
import { getFormattedDate, getFormattedTime, universalAddressToSymbol } from "../../types/util";
import { ROW_FLASH_SX } from "../../styles/realtimeAnimations";
import { isInternalTransfer } from "../../utils/transferUtils";
import { resolveValuation } from '../../utils/resolveValuation';

interface Props {
    transfers: AccountTransfer[];
    account_id: String;
    from: Date;
    to: Date;
    currency: "eur" | "usd" | "chf";
    highlightedIds?: Set<string>;
    isCombined?: boolean;
    accountIds?: string[];
}

export const PortfolioTransfers = ({ transfers, account_id, currency, from, to, highlightedIds, isCombined, accountIds }: Props) => {
    const isInternal = (t: AccountTransfer) =>
        isCombined && !!accountIds && isInternalTransfer(t, accountIds);

    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [csvLoading, setCsvLoading] = useState(false);

    // Reset page when account/data source changes
    useEffect(() => { setPage(0); }, [account_id]);

    // Sort transfers by timestamp descending
    const sortedTransfers = [...transfers].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    // Paginate the sorted transfers
    const paginatedTransfers = sortedTransfers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusIcon = (status: string) => {
        switch (status.toLowerCase()) {
            case "completed":
                return <CheckCircleIcon fontSize="small" color="success" />;
            case "failed":
                return <ErrorIcon fontSize="small" color="error" />;
            case "pending":
                return <PendingIcon fontSize="small" color="warning" />;
            default:
                return <HelpIcon fontSize="small" color="disabled" />;
        }
    };

    const getDirectionInfo = (transfer: AccountTransfer) => {
        const isDeposit = isCombined
            ? accountIds?.includes(transfer.target) && !accountIds?.includes(transfer.source)
            : transfer.target === account_id;
        const isWithdrawal = isCombined
            ? accountIds?.includes(transfer.source) && !accountIds?.includes(transfer.target)
            : transfer.source === account_id;

        if (transfer.transfer_type === "reward") {
            return { icon: <RedeemIcon color="primary" />, label: "Reward", type: "reward" };
        } else if (isDeposit) {
            return { icon: <CallReceivedIcon color="primary" />, label: "Deposit", type: "deposit" };
        } else if (isWithdrawal) {
            return { icon: <CallMadeIcon color="primary" />, label: "Withdraw", type: "withdrawal" };
        } else {
            return { icon: <SwapHorizIcon color="primary" />, label: "Transfer", type: "transfer" };
        }
    };

    const openTransferLink = (transfer: AccountTransfer, accountId: String) => {
        const [location] = accountId.split(":");
        const symbol = universalAddressToSymbol(transfer.asset)

        if (location === "ethereum") {
            window.open(`https://etherscan.io/tx/${transfer.transaction_id}`, "_blank");
        } else if (location === "binance") {
            if (symbol) {
                window.open(`https://www.binance.com/en/trade/${symbol.toUpperCase()}_USDC`, "_blank");
            } else {
                console.warn("Missing symbol for Binance link");
            }
        } else if (location === "kraken") {
            if (symbol) {
                window.open(`https://pro.kraken.com/app/trade/${symbol.toLowerCase()}-eur`, "_blank");
            } else {
                console.warn("Missing symbol for Kraken link");
            }
        } else {
            console.warn(`No handler for transfer location: '${location}'`);
        }
    };

    const generateCsv = () => {
        if (!transfers || transfers.length === 0) return;

        setCsvLoading(true);

        const sortedTransfers = [...transfers].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

        const headers = ['timestamp', 'transaction_id', 'transfer_type', 'asset', 'amount', 'source', 'target', `value-${currency.toLowerCase()}`, `cost-${currency.toLowerCase()}`, 'status'];

        const rows = sortedTransfers.map((transfer) => {
            const value = transfer.valuation?.global?.[currency.toLowerCase()] || 0;
            const costValue = transfer.cost_valuation?.global?.[currency.toLowerCase()] || 0;

            return [
                transfer.timestamp.toISOString(),
                transfer.transaction_id,
                transfer.transfer_type,
                universalAddressToSymbol(transfer.asset),
                transfer.amount.toFixed(8),
                transfer.source,
                transfer.target,
                value.toFixed(2),
                costValue.toFixed(6),
                transfer.status
            ];
        });

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${cell}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `portfolio-${account_id}-transfers-${from.toISOString().split('T')[0]}--${to.toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setCsvLoading(false);
    };

    return (
        <>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SwapHorizIcon fontSize="small" />
                    Transfers
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<GetAppIcon />}
                    disabled={csvLoading || transfers.length === 0}
                    size="small"
                    onClick={generateCsv}
                    sx={{ width: 120 }}
                >
                    CSV
                </Button>
            </Box>
            <AppCard>
                <CardContent sx={{ paddingX: 0, paddingTop: 0, "&:last-child": { paddingBottom: 0 } }}>

                    {/* Column Labels */}
                    <Grid container alignItems="center" spacing={0} sx={{ paddingX: 2, paddingY: 1, backgroundColor: "action.hover" }}>
                        <Grid item xs={1.5}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <SwapHorizIcon fontSize="small" />
                            </Typography>
                        </Grid>
                        {isCombined && (
                            <Grid item xs={1}>
                                <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                    Acct
                                </Typography>
                            </Grid>
                        )}
                        <Grid item xs={2}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <PriceChangeIcon fontSize="small" /> Amount
                            </Typography>
                        </Grid>
                        <Grid item xs={isCombined ? 3.5 : 4.5}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <PersonPinIcon fontSize="small" /> Location / Entity
                            </Typography>
                        </Grid>
                        <Grid item xs={1.5}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <WhatshotIcon fontSize="small" /> Cost
                            </Typography>
                        </Grid>
                        <Grid item xs={1.5}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <TimelapseIcon fontSize="small" /> Status
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ textAlign: "right" }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                                <CalendarTodayIcon fontSize="small" />
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Transfer Data */}
                    {paginatedTransfers.length === 0 ? (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="textSecondary">
                                No transfers available.
                            </Typography>
                        </Box>
                    ) : (
                        <List dense sx={{ padding: 0, margin: 0 }}>
                            {paginatedTransfers.map((transfer, index) => {
                                const directionInfo = getDirectionInfo(transfer);

                                return (
                                    <RecordContextProvider key={transfer.transaction_id} value={transfer}>
                                        <ListItem disableGutters sx={{ padding: 0, margin: 0 }}>
                                            <ListItemButton
                                                sx={{
                                                    flexDirection: "column",
                                                    alignItems: "stretch",
                                                    paddingY: 0.75,
                                                    "&:hover": { backgroundColor: "action.hover" },
                                                    ...(highlightedIds?.has(transfer.transaction_id) ? ROW_FLASH_SX : {}),
                                                }}
                                                onClick={() => openTransferLink(transfer, isCombined ? transfer.account_id : account_id)}
                                            >
                                                <Grid container alignItems="center" spacing={0} sx={{ width: "100%", padding: 0, margin: 0 }}>

                                                    {/* Type (Deposit/Withdraw/Reward) */}
                                                    <Grid item xs={1.5}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                            {directionInfo.icon}
                                                            <Typography variant="body2" color="textPrimary">
                                                                {directionInfo.label}
                                                            </Typography>
                                                            {isInternal(transfer) && (
                                                                <Chip label="Internal" size="small" variant="outlined" sx={{ ml: 0.5, fontSize: '0.65rem', height: 18 }} />
                                                            )}
                                                        </Box>
                                                    </Grid>

                                                    {/* Account */}
                                                    {isCombined && (
                                                        <Grid item xs={1}>
                                                            <Typography variant="caption" color="textSecondary" noWrap title={transfer.account_id}>
                                                                {transfer.account_id.split(':')[0]}
                                                            </Typography>
                                                        </Grid>
                                                    )}

                                                    {/* Amount */}
                                                    <Grid item xs={2}>
                                                        <Typography variant="body2" color="textPrimary">
                                                            {transfer.amount.toFixed(8)} {universalAddressToSymbol(transfer.asset)} {/* {transfer.asset.toUpperCase()} */}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {(() => {
                                                                const v = resolveValuation(transfer.valuation, currency);
                                                                return v != null
                                                                    ? `${v.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${currency.toUpperCase()}`
                                                                    : `0.00 ${currency.toUpperCase()}`;
                                                            })()}
                                                        </Typography>
                                                    </Grid>

                                                    {/* Details */}
                                                    <Grid item xs={isCombined ? 3.5 : 4.5}>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {directionInfo.type === "deposit" ? `${capitalizeFirst(transfer.source)}` :
                                                                directionInfo.type === "withdrawal" ? `${capitalizeFirst(transfer.target)}` :
                                                                    `${capitalizeFirst(transfer.source)} → ${capitalizeFirst(transfer.target)}`}
                                                        </Typography>
                                                    </Grid>

                                                    {/* Cost */}
                                                    <Grid item xs={1.5}>
                                                        <Typography variant="body2" color="textPrimary">
                                                            {Object.keys(transfer.cost).length === 0 ? "-" : (Object.values(transfer.cost)[0]?.toFixed(8) || "N/A")}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {Object.keys(transfer.cost).length === 0
                                                                ? `0,00 ${currency.toUpperCase()}`
                                                                : (() => {
                                                                    const v = resolveValuation(transfer.cost_valuation, currency);
                                                                    return v != null
                                                                        ? `${v.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${currency.toUpperCase()}`
                                                                        : "N/A";
                                                                })()
                                                            }
                                                        </Typography>
                                                    </Grid>

                                                    {/* Status */}
                                                    <Grid item xs={1.5}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                            {getStatusIcon(transfer.status)}
                                                            <Typography variant="caption" color="textSecondary" sx={{ textTransform: "capitalize" }}>
                                                                {transfer.status}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>


                                                    {/* Timestamp */}
                                                    <Grid item xs={1} sx={{ textAlign: "right" }}>
                                                        <Typography variant="body2" color="textPrimary">
                                                            {getFormattedDate(transfer.timestamp)}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {getFormattedTime(transfer.timestamp)}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </ListItemButton>
                                        </ListItem>
                                        {index < paginatedTransfers.length - 1 && <Divider />}
                                    </RecordContextProvider>
                                );
                            })}
                        </List>
                    )}

                    {/* Pagination */}
                    {transfers.length > rowsPerPage && (
                        <TablePagination
                            component="div"
                            count={transfers.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            sx={{ borderTop: 1, borderColor: "divider" }}
                        />
                    )}
                </CardContent>
            </AppCard>
        </>
    );
};